import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CTAButton from '../components/CTAButton';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const {
  FiTarget, FiUsers, FiTrendingUp, FiHeart, FiShield,
  FiZap, FiLayers, FiArrowRight, FiCheck
} = FiIcons;

function About() {
  const values = [
    {
      icon: FiTarget,
      title: 'Practical Solutions',
      description: 'We focus on automation that delivers real results, not flashy tech that sits unused. Every system is built to solve a specific bottleneck in your daily operations.'
    },
    {
      icon: FiUsers,
      title: 'Australian SMB Focus',
      description: 'We understand the unique challenges facing Australian small businesses and trades, from local compliance to the high cost of manual labor.'
    },
    {
      icon: FiTrendingUp,
      title: 'Measurable Impact',
      description: 'Every solution we implement is designed to save time, reduce costs, or increase revenue. We track the numbers so you can see the ROI clearly.'
    },
    {
      icon: FiHeart,
      title: 'Long-term Partnership',
      description: 'We build systems that grow with your business and provide ongoing support. We are your technology partners for the long haul.'
    }
  ];

  return (
    <main className="overflow-x-hidden text-left">
      <SEO
        title="About BPA Solutions - Registered Business Based in Sydney, Australia"
        description="BPA Solutions is a registered business based in Sydney, Australia, providing website design, business automation, CRM automation, and workflow systems for small businesses and trades across Australia."
      />

      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              About BPA Solutions
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              A registered business based in Sydney, Australia. We help small businesses, trades, and
              service providers work smarter through practical automation solutions that deliver real results.
            </p>
            <p className="mt-5 text-xs sm:text-sm text-slate-500">
              Last reviewed <time dateTime="2026-09-16">16 September 2026</time>
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-2xl border border-slate-200 px-6 py-6 sm:px-10 sm:py-8">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="shrink-0">
                <div className="bg-blue-600 p-3 rounded-xl w-fit">
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-white" />
                </div>
              </div>
              <div>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                  BPA Solutions is a registered business based in Sydney, Australia, providing website design,
                  web application development, business automation, CRM automation, reporting dashboards, and
                  workflow systems for small businesses, trades, construction, service businesses, and professional
                  services across Australia.
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {[
                    'Sydney-based, servicing businesses across Australia',
                    'Website design and custom web applications',
                    'Business automation, CRM, dashboards, and integrations',
                    'Built for small business, trades, construction, and service businesses'
                  ].map((point, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 font-medium">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-500 shrink-0" />
                      {point}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-slate prose-lg max-w-none">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 tracking-tight">
              The BPA Solutions Mission
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              BPAS was founded after seeing too many Australian small businesses drowning in administrative
              tasks that could be easily automated. While big corporations had entire IT departments to
              streamline their operations, small businesses were left managing everything manually - often
              working 60+ hour weeks just to keep up with the paperwork.
            </p>
            <p className="text-slate-600 mb-6 leading-relaxed">
              We realized there was a massive gap in the market. Most automation consultants focused on
              enterprise clients, leaving small businesses with generic, one-size-fits-all solutions that
              did not address the unique challenges of local trades and service providers. That is why we
              created BPAS. We specialize exclusively in automation solutions for Australian small businesses
              - from trades and field services to NDIS providers and professional services.
            </p>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Every system we build is designed with the reality of small business operations in mind. We
              focus on tools that Australian owners already use and trust, such as Xero, ServiceM8, and
              Microsoft 365. Our goal is to replace your spreadsheets with systems that actually talk to
              each other, saving you up to 15 hours of admin per week.
            </p>
            <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 mb-8">
              <h3 className="text-blue-900 font-bold mb-4 text-left">Our Local Commitment</h3>
              <p className="text-blue-800 text-sm text-left leading-relaxed">
                We are a registered, Sydney-based Australian business. We do not outsource our core
                consulting; we work directly with you to understand your business flow. Whether you are
                in Sydney, Melbourne, Brisbane, or regional Australia, we provide the same high-level
                support and local expertise that small businesses need to thrive.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 tracking-tight">
              What Drives Our Strategy
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              We believe every Australian small business deserves access to the same automation advantages
              that big corporations have enjoyed for years.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200">
                <div className="bg-blue-50 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  <SafeIcon icon={value.icon} className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">{value.title}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10 text-center">
            Our Automation Methodology
          </h2>
          <div className="space-y-12">
            {[
              {
                title: '1. The Automation Audit',
                text: 'We start by mapping your current manual processes in detail. We look for low-hanging fruit - tasks that take the most time but require the least amount of human decision-making. This usually includes data entry, quote follow-ups, and appointment scheduling.'
              },
              {
                title: '2. System Integration',
                text: 'We link your existing tools together. Instead of buying new, expensive enterprise software, we make your current stack (like Xero, Google Workspace, and your CRM) talk to each other. This eliminates the digital islands that cause double-handling of data.'
              },
              {
                title: '3. Deployment and Training',
                text: 'We build and test your automations in a safe environment before going live. Once ready, we provide simple, practical training for you and your team to ensure the new systems are actually used and deliver the promised time savings.'
              }
            ].map((step, i) => (
              <div key={i} className="flex flex-col md:flex-row gap-6 items-start">
                <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-10">Explore Our Expertise</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/services" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiLayers} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Our Services</h3>
              <p className="text-sm text-slate-500 mb-4">Browse our full range of automation and web solutions.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                View Services <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
              </span>
            </Link>
            <Link to="/blog" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiZap} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Automation Blog</h3>
              <p className="text-sm text-slate-500 mb-4">Read our latest articles on scaling small businesses.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                Read Blog <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
              </span>
            </Link>
            <Link to="/resources" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Free Resources</h3>
              <p className="text-sm text-slate-500 mb-4">Download checklists and templates to start automating.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                View Tools <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
              </span>
            </Link>
            <Link to="/contact" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiTarget} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Contact Us</h3>
              <p className="text-sm text-slate-500 mb-4">Book your free 20-minute automation consultation.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                Get in Touch <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 tracking-tight">
            Ready to Work Smarter?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let's discuss how automation can transform your business operations.
          </p>
          <CTAButton size="lg" className="w-full sm:w-auto px-12">
            Book a Free Consult
          </CTAButton>
        </div>
      </section>
    </main>
  );
}

export default About;